import { useState } from 'react';
import { ExternalLink, HelpingHand, Star, Loader2, ShieldCheck, Info } from 'lucide-react';
import { energyControlMonthlyLabel } from '../../config/pricing';
import DriverStatusBadge, { getDriverLabel } from '../admin/DriverStatusBadge';
import usePublicDrivers from '../../hooks/usePublicDrivers';
import { matchDriverForProduct } from '../../lib/matchDriverForProduct';
import { getCategoryProfile } from '../../lib/categoryProfile';

/**
 * isSafeExternalUrl — STRICT guard. Phase 9.x2 hardened version that mirrors
 * the server-side `is_valid_external_product_url`. Returns true ONLY for
 * real, deliverable absolute http(s) URLs that are unambiguously NOT a
 * placeholder. Anything else falls back to the inline advice form so a
 * customer can NEVER reach a black/empty screen.
 *
 * Single source of truth: this function and its server twin must stay in sync.
 */
const UNSAFE_HOSTS = [
  'example.com', 'example.org', 'example.net',
  'test.com', 'localhost', '127.0.0.1', '0.0.0.0',
  'placeholder.invalid', 'your-domain.com',
];
const UNSAFE_HOST_SUFFIXES = [
  'preview.emergent',
  'preview.emergentagent.com',
  'emergentagent.com',
];
// powersure-worldwide.com does not (yet) host product detail pages.
// Any URL pointing there from the catalog is treated as a placeholder.
const PLACEHOLDER_PRODUCT_HOST = 'powersure-worldwide.com';

export function isSafeExternalUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === '#' || lower === '/' || lower === 'about:blank') return false;
  if (lower.startsWith('javascript:') || lower.startsWith('data:')
      || lower.startsWith('vbscript:') || lower.startsWith('file:')) return false;
  if (!lower.startsWith('https://') && !lower.startsWith('http://')) return false;
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }
  const host = (parsed.hostname || '').toLowerCase();
  if (!host || !host.includes('.')) return false;
  if (UNSAFE_HOSTS.includes(host)) return false;
  if (UNSAFE_HOST_SUFFIXES.some((suf) => host.endsWith(suf))) return false;
  if (host === PLACEHOLDER_PRODUCT_HOST || host.endsWith(`.${PLACEHOLDER_PRODUCT_HOST}`)) return false;
  return true;
}

/**
 * ProductCard — compact, clickable product block.
 * Mobile-first: full-width below sm, 1 col on sm, 2 cols on lg.
 * Props:
 *  - product: { product_id, name, reason_short, audience, price_indication,
 *               compat_note, product_url, inquiry_cta }
 *  - featured: true to show the "Top match" chip
 *  - tone: 'first_step' (default, direct CTA) | 'next_step' (softer copy —
 *          "Bekijk als mogelijke optie" / "Vraag advies over deze optie")
 *  - onInquiry: (product_id) => void — opens the lead form scrolled into view
 */
export default function ProductCard({ product, featured = false, tone = 'first_step', onInquiry, customerEmail, leadId, intakeId }) {
  const {
    product_id,
    name,
    reason_short,
    audience,
    price_indication,
    compat_note,
    product_url,
    inquiry_cta,
  } = product || {};

  // Driver-aware label (Phase B). We match against the public driver list —
  // server-side already guarantees only approved+visible+safe drivers come
  // through, so any driver we find here is safe to show to a customer.
  const { drivers } = usePublicDrivers();
  const driver = matchDriverForProduct(product, drivers);
  const driverLabel = driver ? getDriverLabel(driver) : 'Handmatige route beschikbaar';
  const profile = getCategoryProfile(product, driver);

  // Track the first paint of this card so we can analyse driver coverage.
  // We only fire once per product_id per mount via the inline guard below.
  const [tracked, setTracked] = useState(false);
  if (!tracked && product_id) {
    setTracked(true);
    if (window.pwTrack) {
      window.pwTrack('product_view', {
        product_id,
        component_type: driver?.component_type || product?.component_type || null,
        driver_id: driver?.driver_id || null,
        segment: 'residential',
        intake_id: intakeId || null,
      });
      window.pwTrack('cta_view', {
        product_id,
        cta_kind: profile.kind,
        primary_label: profile.primaryLabel,
        component_type: driver?.component_type || product?.component_type || null,
        driver_id: driver?.driver_id || null,
        intake_id: intakeId || null,
      });
    }
  }

  // Server-side validation has the final word when it travels with the
  // product. Only fall back to local validation if the field is missing
  // (older API responses or test fixtures).
  const serverFlag = product?.is_valid_product_url;
  const hasExternal =
    serverFlag === true
      ? isSafeExternalUrl(product_url) // double-check on the client too
      : serverFlag === false
        ? false
        : isSafeExternalUrl(product_url);
  const isNextStep = tone === 'next_step';
  // Phase 11 Fase 2b — dedicated subscription-checkout CTA for the
  // Powersure monitoring product. Bypasses the standard advice-form
  // fallback with a Stripe subscription flow.
  const isMonitoring = product_id === 'powersure_monitoring';
  const [subLoading, setSubLoading] = useState(false);

  const startMonitoringCheckout = async () => {
    if (subLoading) return;
    setSubLoading(true);
    try {
      const res = await fetch('/api/subscriptions/monitoring/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: customerEmail || null,
          lead_id: leadId || null,
          intake_id: intakeId || null,
        }),
      });
      if (!res.ok) {
        // Graceful fail-safe — never navigate to black screen.
        const detail = await res.text();
        console.warn('[monitoring-checkout] failed:', res.status, detail.slice(0, 200));
        alert('Monitoring checkout is momenteel niet beschikbaar. Wij nemen contact met u op via het adviesformulier.');
        onInquiry && onInquiry(product_id);
        return;
      }
      const data = await res.json();
      if (!data?.url) {
        onInquiry && onInquiry(product_id);
        return;
      }
      if (window.pwTrack) window.pwTrack('monitoring_subscription_start', { product_id, amount: data.amount });
      window.location.href = data.url;
    } catch (err) {
      console.warn('[monitoring-checkout] network error:', err);
      alert('Kon monitoring checkout niet starten. Probeer opnieuw of vraag advies.');
      onInquiry && onInquiry(product_id);
    } finally {
      setSubLoading(false);
    }
  };

  // Commercially softer copy for next-step products. Phase C.2: profile.inquiryLabel
  // overrides for installer products (e.g. "Niet relevant") and measure products
  // (e.g. "Installatiehulp nodig?").
  const externalLabel = profile.kind === 'installer' ? profile.primaryLabel
    : (isNextStep ? 'Bekijk als mogelijke optie' : 'Bekijk product');
  const inquiryLabel = profile.inquiryLabel
    || (isNextStep ? 'Vraag advies over deze optie' : inquiry_cta || 'Vraag hulp aan');

  return (
    <article
      className={[
        'relative flex flex-col rounded-2xl border-2 bg-white overflow-hidden transition-shadow',
        'hover:shadow-md',
        featured ? 'border-emerald-300 shadow-sm' : 'border-border',
      ].join(' ')}
      data-testid={`ec-sk-product-${product_id}`}
    >
      {featured && (
        <div
          className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-semibold shadow-sm"
          data-testid={`ec-sk-product-featured-${product_id}`}
        >
          <Star className="w-3 h-3 fill-white" />
          Top match
        </div>
      )}

      <div className="p-5 flex-1 flex flex-col">
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`ec-sk-product-driverbadge-${product_id}`}>
          {driver ? (
            <DriverStatusBadge driver={driver} />
          ) : (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border bg-zinc-100 text-zinc-700 border-zinc-200 text-xs px-2 py-1"
              data-testid={`ec-sk-product-driverbadge-fallback-${product_id}`}
              title="Geen automatische integratie. U regelt dit zelf — Powersure helpt met advies."
            >
              Handmatige route beschikbaar
            </span>
          )}
        </div>

        <h3
          className="text-[17px] sm:text-[18px] font-semibold text-primary leading-tight pr-20"
          style={{ fontFamily: 'Outfit' }}
          data-testid={`ec-sk-product-name-${product_id}`}
        >
          {name}
        </h3>

        {reason_short && (
          <p className="mt-2 text-[14.5px] leading-relaxed text-foreground">
            {reason_short}
          </p>
        )}

        {profile.microcopy && (
          <p
            className="mt-2 text-[13px] leading-relaxed text-muted-foreground"
            data-testid={`ec-sk-product-microcopy-${product_id}`}
          >
            {profile.microcopy}
          </p>
        )}

        {profile.warning && (
          <div
            className="mt-3 flex items-start gap-2 text-[12.5px] leading-relaxed bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900"
            data-testid={`ec-sk-product-warning-${product_id}`}
          >
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{profile.warning}</span>
          </div>
        )}

        <dl className="mt-4 space-y-2 text-[13px] leading-snug">
          {audience && (
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0 w-[90px]">Voor wie</dt>
              <dd className="text-foreground">{audience}</dd>
            </div>
          )}
          {price_indication && (
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0 w-[90px]">Prijs</dt>
              <dd className="text-foreground font-medium" data-testid={`ec-sk-product-price-${product_id}`}>
                {price_indication}
              </dd>
            </div>
          )}
          {compat_note && (
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0 w-[90px]">Let op</dt>
              <dd className="text-foreground flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 mt-0.5 flex-shrink-0" />
                <span>{compat_note}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {!hasExternal && (
        <div
          className="mx-5 mb-3 mt-1 flex items-start gap-2 text-[12px] leading-relaxed bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900"
          data-testid={`ec-sk-product-fallback-note-${product_id}`}
        >
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Deze productlink wordt nog gecontroleerd. Vraag advies en wij sturen de beste optie mee.
          </span>
        </div>
      )}

      <div className="px-5 pb-5 pt-3 border-t border-border bg-muted/20 flex flex-col sm:flex-row gap-2">
        {isMonitoring ? (
          <div className="flex-1 flex flex-col gap-1.5">
            <button
              type="button"
              onClick={startMonitoringCheckout}
              disabled={subLoading}
              className="btn-primary inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-60"
              data-testid={`ec-sk-monitoring-subscribe`}
              data-rendered-action="subscription_checkout"
            >
              {subLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              {subLoading ? 'Bezig…' : `Start maandelijkse monitoring — ${energyControlMonthlyLabel()}`}
            </button>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Maandelijks opzegbaar. Geen besparingsgarantie. Powersure kijkt maandelijks mee en
              stuurt concrete verbeterkansen.
            </p>
          </div>
        ) : hasExternal ? (
          <a
            href={product_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold flex-1"
            data-testid={`ec-sk-product-view-${product_id}`}
            data-rendered-action="external_link"
            onClick={() => {
              if (window.pwTrack) {
                window.pwTrack('ec_sk_product_view', { product_id, tone });
                window.pwTrack('affiliate_click', {
                  product_id,
                  driver_id: driver?.driver_id || null,
                  component_type: driver?.component_type || null,
                  segment: 'residential',
                  intake_id: intakeId || null,
                });
              }
            }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {externalLabel}
          </a>
        ) : (
          // Phase 9.x2 — strictly type="button". Never an <a>, never an
          // <a href="#">, never a <Link>. Clicking opens the inline advice
          // form. There is NO navigation path from this branch.
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (window.pwTrack) {
                window.pwTrack('manual_route_selected', {
                  product_id,
                  driver_id: driver?.driver_id || null,
                  component_type: driver?.component_type || null,
                  intake_id: intakeId || null,
                });
              }
              onInquiry && onInquiry(product_id);
            }}
            className="btn-primary inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold flex-1"
            data-testid={`ec-sk-product-view-${product_id}`}
            data-rendered-action="advice_form"
          >
            <HelpingHand className="w-3.5 h-3.5" />
            Vraag advies over deze optie
          </button>
        )}

        {!isMonitoring && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (window.pwTrack) {
                window.pwTrack('product_cta_clicked', {
                  product_id,
                  cta: 'inquiry',
                  cta_kind: profile.kind,
                  cta_label: inquiryLabel,
                  driver_id: driver?.driver_id || null,
                  component_type: driver?.component_type || product?.component_type || null,
                  intake_id: intakeId || null,
                  segment: 'residential',
                });
                if (profile.kind === 'installer') {
                  window.pwTrack('quote_started', {
                    product_id,
                    component_type: product?.component_type || driver?.component_type || null,
                    driver_id: driver?.driver_id || null,
                    intake_id: intakeId || null,
                    segment: 'residential',
                  });
                }
              }
              onInquiry && onInquiry(product_id);
            }}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold border-2 border-primary text-primary bg-white hover:bg-primary/5 flex-1"
            data-testid={`ec-sk-product-inquiry-${product_id}`}
            data-rendered-action="advice_form"
          >
            <HelpingHand className="w-3.5 h-3.5" />
            {inquiryLabel}
          </button>
        )}
      </div>
    </article>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="rounded-2xl border-2 border-border bg-white p-5 animate-pulse">
      <div className="h-5 w-2/3 bg-muted rounded" />
      <div className="mt-3 space-y-2">
        <div className="h-3 w-full bg-muted rounded" />
        <div className="h-3 w-5/6 bg-muted rounded" />
      </div>
      <div className="mt-4 h-9 w-full bg-muted rounded" />
    </div>
  );
}

export function ProductsLoading({ count = 3 }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
      <Loader2 className="w-4 h-4 animate-spin" />
      Aanbevelingen worden geladen…
      <div className="sr-only">{count}</div>
    </div>
  );
}
